/**
 * AdSense In-Article Widget Web Component
 * 
 * IMPORTANT: By default, this component will NOT render anything in production
 * unless explicitly enabled with data-ads-enabled="true"
 * 
 * Usage in production (default - NO ads rendered, component hidden):
 * <ad-widget></ad-widget>
 * <ad-widget data-ad-layout="banner"></ad-widget>
 * 
 * Usage with REAL Google Ads (loads Google Ads JavaScript and shows ads):
 * <ad-widget 
 *   data-ads-enabled="true"
 *   data-ad-client="ca-pub-XXXXXXXXXXXXXXXX"
 *   data-ad-slot="1234567890"
 *   data-ad-format="fluid"
 *   data-ad-layout="in-article"
 *   data-full-width-responsive="true">
 * </ad-widget>
 * 
 * Banner ad with real Google Ads:
 * <ad-widget 
 *   data-ads-enabled="true"
 *   data-ad-type="banner"
 *   data-ad-client="ca-pub-XXXXXXXXXXXXXXXX"
 *   data-ad-slot="1234567890">
 * </ad-widget>
 * 
 * For development/testing with fake placeholder ad:
 * <ad-widget data-fake="true"></ad-widget>
 * 
 * Behavior:
 * - Localhost/127.0.0.1: Shows placeholder/fake ads for development
 * - Production without data-ads-enabled="true": NO ads rendered (component hidden)
 * - Production with data-ads-enabled="true": Loads Google Ads JavaScript and shows real ads
 * - Explicit data-fake="true": Always shows placeholder ads (any environment)
 */

class AdWidget extends HTMLElement {
  static get observedAttributes() {
    return [
      'data-ad-client',
      'data-ad-slot',
      'data-ad-format',
      'data-ad-layout',
      'data-ad-type',
      'data-full-width-responsive',
      'data-fake',
      'data-ads-enabled'
    ];
  }

  constructor() {
    super();
    this.adScriptLoaded = false;
  }

  connectedCallback() {
    // Check if fake mode is enabled (only on localhost for development)
    const isLocalhost = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
    const fakeAttr = this.getAttribute('data-fake') === 'true';
    
    // IMPORTANT: Check if ads are enabled via data attribute    
    // Set data-ads-enabled="true" on the widget when you want to use real Google Ads
    const adsEnabled = this.getAttribute('data-ads-enabled') === 'true';
    
    // Only render ads if:
    // 1. Ads are explicitly enabled (data-ads-enabled="true") - for real ads
    // 2. OR on localhost - for fake ads during development
    // 3. OR explicitly set to fake mode - for testing
    if (!adsEnabled && !isLocalhost && !fakeAttr) {
      // Production without ads enabled - don't render anything
      this.style.display = 'none';
      return;
    }
    
    // Determine if we should show fake or real ads
    const isFake = fakeAttr || isLocalhost;
    
    // Only load Google Ads script if ads are enabled and not in fake mode
    if (!isFake && adsEnabled) {
      this._loadAdSenseScript();
    }

    // Render (either fake or real ads)
    this._render();
  }

  attributeChangedCallback() {
    if (this.isConnected) {
      this._render();
    }
  }

  /**
   * Load Google AdSense script if not already loaded
   */
  _loadAdSenseScript() {
    // Check if AdSense script is already loaded
    if (window.adsbygoogle || this.adScriptLoaded) {
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="adsbygoogle.js"]');
    if (existingScript) {
      this.adScriptLoaded = true;
      return;
    }

    // Create and load the AdSense script
    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    
    // Get ad-client from attribute or use default
    const adClient = this.getAttribute('data-ad-client') || 'ca-pub-XXXXXXXXXXXXXXXX';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adClient}`;
    
    script.onerror = () => {
      console.warn('AdSense: Failed to load AdSense script. Ad blocker may be active.');
    };

    document.head.appendChild(script);
    this.adScriptLoaded = true;
  }

  /**
   * Render the ad unit
   */
  _render() {
    // Check if fake mode is enabled (only on localhost or explicit fake)
    const isLocalhost = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
    const fakeAttr = this.getAttribute('data-fake') === 'true';
    const adsEnabled = this.getAttribute('data-ads-enabled') === 'true';
    
    // Show fake ads on localhost or when explicitly set to fake
    const isFake = fakeAttr || isLocalhost;
    
    if (isFake) {
      this._renderFakeAd();
      return; // Exit early, don't render real ad
    }

    // If we get here, ads must be enabled (checked in connectedCallback)
    // Check ad type (support both data-ad-type and data-ad-layout)
    const adType = this.getAttribute('data-ad-type') || this.getAttribute('data-ad-layout') || 'in-article';
    
    if (adType === 'banner') {
      this._renderBannerAd();
    } else {
      this._renderInArticleAd();
    }
  }

  /**
   * Render in-article ad (default)
   */
  _renderInArticleAd() {
    // Get attributes or use defaults
    const adClient = this.getAttribute('data-ad-client') || 'ca-pub-XXXXXXXXXXXXXXXX';
    const adSlot = this.getAttribute('data-ad-slot') || '1234567890';
    const adFormat = this.getAttribute('data-ad-format') || 'fluid';
    const adLayout = this.getAttribute('data-ad-layout') || 'in-article';
    const fullWidthResponsive = this.getAttribute('data-full-width-responsive') || 'true';

    // Create the ad container
    this.innerHTML = `
      <div class="ad-widget-container">
        <ins class="adsbygoogle"
             style="display:block; text-align:center;"
             data-ad-client="${this._escapeHtml(adClient)}"
             data-ad-slot="${this._escapeHtml(adSlot)}"
             data-ad-format="${this._escapeHtml(adFormat)}"
             data-ad-layout="${this._escapeHtml(adLayout)}"
             data-full-width-responsive="${this._escapeHtml(fullWidthResponsive)}">
        </ins>
      </div>
    `;

    this._pushAd();
  }

  /**
   * Render banner ad (horizontal)
   */
  _renderBannerAd() {
    const adClient = this.getAttribute('data-ad-client') || 'ca-pub-XXXXXXXXXXXXXXXX';
    const adSlot = this.getAttribute('data-ad-slot') || '1234567890';
    const adFormat = this.getAttribute('data-ad-format') || 'auto';
    const fullWidthResponsive = this.getAttribute('data-full-width-responsive') || 'true';

    // Create the banner ad container
    this.innerHTML = `
      <div class="ad-widget-container ad-widget-banner">
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="${this._escapeHtml(adClient)}"
             data-ad-slot="${this._escapeHtml(adSlot)}"
             data-ad-format="${this._escapeHtml(adFormat)}"
             data-full-width-responsive="${this._escapeHtml(fullWidthResponsive)}">
        </ins>
      </div>
    `;

    this._pushAd();
  }

  /**
   * Push ad to AdSense
   */
  _pushAd() {
    // Push the ad after a short delay to ensure DOM is ready
    setTimeout(() => {
      try {
        if (window.adsbygoogle && window.adsbygoogle.loaded) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } else if (window.adsbygoogle) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
      } catch (error) {
        console.warn('AdSense: Error pushing ad', error);
      }
    }, 100);
  }

  /**
   * Render a fake placeholder ad for development/testing
   */
  _renderFakeAd() {
    // Support both data-ad-type and data-ad-layout
    const adType = this.getAttribute('data-ad-type') || this.getAttribute('data-ad-layout') || 'in-article';
    const isBanner = adType === 'banner';
    
    const fakeAdMessages = [
      '🎯 Your Ad Here',
      '📢 Advertisement Placeholder',
      '🎨 Sponsored Content',
      '💼 Partner Message',
      '🌟 Featured Content'
    ];
    
    const randomMessage = fakeAdMessages[Math.floor(Math.random() * fakeAdMessages.length)];
    
    // Real AdSense ad sizes
    // Banner: 728x90 (desktop), 320x50 (mobile)
    // In-article: Responsive but typically ~300x250 or larger
    const adWidth = isBanner ? '100%' : '300px';
    const adHeight = isBanner ? '90px' : '280px';
    const adSizeText = isBanner ? '728x90 / Leaderboard' : '300x280 / Medium Rectangle';
    const padding = isBanner ? '0' : '1.5rem';
    const maxWidth = isBanner ? '728px' : '300px';
    
    this.innerHTML = `
      <div class="ad-widget-container ad-widget-fake ${isBanner ? 'ad-widget-banner' : ''}">
        <div class="fake-ad-content">
          <div class="fake-ad-icon">${randomMessage.split(' ')[0]}</div>
          <div class="fake-ad-title">${randomMessage}</div>
          <div class="fake-ad-size">${adSizeText}</div>
          <div class="fake-ad-label">DEVELOPMENT MODE</div>
        </div>
      </div>
      <style>
        .ad-widget-fake {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          border: 2px dashed rgba(255, 255, 255, 0.3);
          margin: 0 auto;
          box-sizing: border-box;
        }
        
        /* In-article ad (square/vertical) - default */
        .ad-widget-fake:not(.ad-widget-banner) {
          width: 300px !important;
          max-width: 300px !important;
          height: 280px !important;
          padding: 1.5rem !important;
        }
        
        /* Banner ad (horizontal) */
        .ad-widget-fake.ad-widget-banner {
          width: 100% !important;
          max-width: 728px !important;
          height: 90px !important;
          padding: 0 !important;
        }
        
        .ad-widget-fake.ad-widget-banner .fake-ad-content {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0 1.5rem;
        }
        
        .ad-widget-fake.ad-widget-banner .fake-ad-icon {
          font-size: 2rem;
          margin-bottom: 0;
          animation: none;
        }
        
        .ad-widget-fake.ad-widget-banner .fake-ad-title {
          font-size: 1rem;
          margin-bottom: 0;
        }
        
        .ad-widget-fake.ad-widget-banner .fake-ad-size {
          margin-bottom: 0;
          font-size: 0.75rem;
        }
        
        .ad-widget-fake.ad-widget-banner .fake-ad-label {
          margin-top: 0;
          margin-left: auto;
          font-size: 0.625rem;
          padding: 0.2rem 0.5rem;
        }
        
        .fake-ad-content {
          text-align: center;
          color: white;
        }
        
        .fake-ad-icon {
          font-size: 2.5rem;
          margin-bottom: 0.75rem;
          animation: pulse 2s ease-in-out infinite;
        }
        
        .fake-ad-title {
          font-size: 1.125rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .fake-ad-size {
          font-size: 0.75rem;
          opacity: 0.9;
          margin-bottom: 0.5rem;
          font-family: 'Courier New', monospace;
        }
        
        .fake-ad-label {
          font-size: 0.625rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          opacity: 0.8;
          margin-top: 0.75rem;
          padding: 0.2rem 0.6rem;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: inline-block;
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
        
        /* Mobile: Banner becomes 320x50 */
        @media (max-width: 768px) {
          .ad-widget-fake.ad-widget-banner {
            max-width: 320px !important;
            height: 50px !important;
          }
          
          .ad-widget-fake.ad-widget-banner .fake-ad-content {
            gap: 0.5rem;
            padding: 0 1rem;
          }
          
          .ad-widget-fake.ad-widget-banner .fake-ad-icon {
            font-size: 1.25rem;
          }
          
          .ad-widget-fake.ad-widget-banner .fake-ad-title {
            font-size: 0.75rem;
          }
          
          .ad-widget-fake.ad-widget-banner .fake-ad-size {
            font-size: 0.625rem;
          }
          
          .ad-widget-fake.ad-widget-banner .fake-ad-label {
            font-size: 0.5rem;
            padding: 0.15rem 0.4rem;
          }
          
          /* In-article stays same size on mobile (300x250 is standard) */
          .ad-widget-fake:not(.ad-widget-banner) {
            width: 300px !important;
            height: 250px !important;
          }
          
          .ad-widget-fake:not(.ad-widget-banner) .fake-ad-icon {
            font-size: 2rem;
          }
          
          .ad-widget-fake:not(.ad-widget-banner) .fake-ad-title {
            font-size: 1rem;
          }
        }
      </style>
    `;
  }

  /**
   * Escape HTML to prevent XSS
   */
  _escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Define the custom element
if (!customElements.get('ad-widget')) {
  customElements.define('ad-widget', AdWidget);
}

// Export for ES module usage
export default AdWidget;

